/**
 * PlanSelector
 * 
 * Exibe os 3 planos (Basic/Pro/Enterprise) com preços mensais/anuais
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";

interface Plan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  recommended?: boolean;
}

// Mapeamento dos Price IDs do Stripe
const stripePriceIds: Record<string, string> = {
  basic_monthly: "price_1SNhVGRiELkPLD5mypxxv8e6",
  basic_yearly: "price_1SNhV2RiELkPLD5mbQVCWgIt",
  pro_monthly: "price_TODO_PRO_MONTHLY",   // TODO: Adicionar Price ID mensal
  pro_yearly: "price_TODO_PRO_YEARLY",     // TODO: Adicionar Price ID anual
  enterprise_monthly: "price_TODO_ENT_MONTHLY", // TODO: Adicionar Price ID mensal
  enterprise_yearly: "price_TODO_ENT_YEARLY",   // TODO: Adicionar Price ID anual
};

const plans: Plan[] = [
  {
    id: "basic",
    name: "Basic",
    description: "Para quem está começando",
    monthlyPrice: 39.99,
    yearlyPrice: 383.90, // ~R$ 31,99/mês (20% OFF)
    features: [
      "1 usuário",
      "Até 100 produtos",
      "Relatórios básicos",
      "Suporte por email",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    description: "Para negócios em crescimento",
    monthlyPrice: 69.99,
    yearlyPrice: 671.90, // ~R$ 55,99/mês (20% OFF)
    features: [
      "Até 5 usuários",
      "Produtos ilimitados",
      "Relatórios avançados",
      "Suporte prioritário",
      "Integração WhatsApp",
    ],
    recommended: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "Para grandes operações",
    monthlyPrice: 99.99,
    yearlyPrice: 959.90, // ~R$ 79,99/mês (20% OFF)
    features: [
      "Usuários ilimitados",
      "Tudo do Pro +",
      "API de integração",
      "Suporte 24/7",
      "Treinamento personalizado",
    ],
  },
];

interface PlanSelectorProps {
  onSelectPlan: (planId: string, interval: "month" | "year") => void;
  isLoading?: boolean;
}

export function PlanSelector({ onSelectPlan, isLoading }: PlanSelectorProps) {
  const [isYearly, setIsYearly] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Toggle Mensal/Anual */}
      <div className="flex items-center justify-center gap-3">
        <Label htmlFor="billing-toggle" className={!isYearly ? "font-semibold" : ""}>
          Mensal
        </Label>
        <Switch
          id="billing-toggle"
          checked={isYearly}
          onCheckedChange={setIsYearly}
        />
        <Label htmlFor="billing-toggle" className={isYearly ? "font-semibold" : ""}>
          Anual
          <Badge variant="secondary" className="ml-2">
            20% OFF
          </Badge>
        </Label>
      </div>

      {/* Cards de Planos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
          const pricePerMonth = isYearly ? price / 12 : price;

          return (
            <Card
              key={plan.id}
              className={`relative ${plan.recommended ? "border-primary shadow-lg" : ""}`}
            >
              {plan.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                  Recomendado
                </Badge>
              )}

              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Preço - altura fixa para evitar movimento */}
                <div className="min-h-[120px]">
                  <div className="text-4xl font-bold">
                    {formatPrice(pricePerMonth)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">
                    {isYearly ? "por mês (cobrado anualmente)" : "por mês"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 h-5">
                    {isYearly && (
                      <>Total: {formatPrice(price)}/ano</>
                    )}
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Botão */}
                <Button
                  className="w-full"
                  variant={plan.recommended ? "default" : "outline"}
                  onClick={() => {
                    const priceKey = `${plan.id}_${isYearly ? "yearly" : "monthly"}`;
                    const stripePriceId = stripePriceIds[priceKey];
                    
                    // Validar se o price ID está configurado
                    if (stripePriceId.startsWith('price_TODO_')) {
                      alert('Este plano ainda não está disponível. Por favor, escolha o plano Basic.');
                      return;
                    }
                    
                    onSelectPlan(stripePriceId, isYearly ? "year" : "month");
                  }}
                  disabled={isLoading || stripePriceIds[`${plan.id}_${isYearly ? "yearly" : "monthly"}`].startsWith('price_TODO_')}
                >
                  {stripePriceIds[`${plan.id}_${isYearly ? "yearly" : "monthly"}`].startsWith('price_TODO_') 
                    ? "Em breve" 
                    : (isLoading ? "Processando..." : "Escolher plano")}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Aviso Trial */}
      <div className="text-center text-sm text-muted-foreground">
        🎉 Trial grátis de 60 dias • Cancele quando quiser
      </div>
    </div>
  );
}
